import React, { useState } from 'react';
import { useStore } from '@/state/store';
import { pageDisplayName } from '@/lib/pageNames';
import { useI18n } from '@/i18n/I18nContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Plus, X, Network, FileSpreadsheet, Cable, MapPin } from 'lucide-react';

export const PagesSidebar = () => {
  const { t, language } = useI18n();
  const doc = useStore((s) => s.doc);
  const activePageId = useStore((s) => s.activePageId);
  const setActivePage = useStore((s) => s.setActivePage);
  const addPage = useStore((s) => s.addPage);
  const addDetailPage = useStore((s) => s.addDetailPage);
  const addLocationPage = useStore((s) => s.addLocationPage);
  const renamePage = useStore((s) => s.renamePage);
  const deletePage = useStore((s) => s.deletePage);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditValue(pageDisplayName(p.name, language));
  };
  const commitEdit = () => {
    if (editingId && editValue.trim()) renamePage(editingId, editValue.trim());
    setEditingId(null);
  };

  return (
    <div
      className="flex h-11 items-center gap-1 border-t border-border bg-[#FBFBFA] px-2"
      data-testid="pages-sidebar"
    >
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {doc.pages.map((p) => {
          const active = p.id === activePageId;
          return (
            <div
              key={p.id}
              className={`group flex h-8 shrink-0 items-center gap-1 rounded-md border px-2.5 text-sm transition-colors ${
                active ? 'border-[#0B5FFF]/40 bg-white text-[#111418] shadow-sm' : 'border-transparent text-muted-foreground hover:bg-white/70'
              }`}
            >
              {editingId === p.id ? (
                <input
                  autoFocus
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  className="w-24 bg-transparent text-sm outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setActivePage(p.id)}
                  onDoubleClick={() => startEdit(p)}
                  className="flex max-w-[160px] items-center gap-1.5 truncate"
                  data-testid={`page-tab-${p.id}`}
                >
                  {p.type === 'detail' && <Cable className="h-3.5 w-3.5 shrink-0 text-[#0F7B6C]" />}
                  {p.type === 'location' && <MapPin className="h-3.5 w-3.5 shrink-0 text-[#B45309]" />}
                  <span className="truncate">{pageDisplayName(p.name, language)}</span>
                </button>
              )}
              {doc.pages.length > 1 && (
                <button
                  type="button"
                  onClick={() => window.confirm(t('confirm_delete_page')) && deletePage(p.id)}
                  className="ml-0.5 hidden rounded p-0.5 text-muted-foreground hover:bg-black/5 hover:text-[#B42318] group-hover:block"
                  aria-label={t('delete')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 shrink-0 px-2" data-testid="pages-add-button" title={t('add_page')}>
              <Plus className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={addPage} data-testid="pages-add-standard">
              <Network className="mr-2 h-4 w-4" /> {t('standard_page')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={addDetailPage} data-testid="pages-add-detail">
              <Cable className="mr-2 h-4 w-4" /> {t('detail_page')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={addLocationPage} data-testid="pages-add-location">
              <MapPin className="mr-2 h-4 w-4" /> {t('location_plan')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mx-1 h-6 w-px bg-border" />
      <span className="hidden px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:inline">
        {t('auto_pages')}
      </span>
      <AutoTab
        active={activePageId === 'auto-cablelist'}
        onClick={() => setActivePage('auto-cablelist')}
        icon={<Cable className="h-4 w-4" />}
        label={t('cable_list')}
        testid="page-tab-auto-cablelist"
      />
      <AutoTab
        active={activePageId === 'auto-vlanlist'}
        onClick={() => setActivePage('auto-vlanlist')}
        icon={<FileSpreadsheet className="h-4 w-4" />}
        label={t('vlan_list')}
        testid="page-tab-auto-vlanlist"
      />
    </div>
  );
};

const AutoTab = ({ active, onClick, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors ${
      active ? 'border-[#0B5FFF]/40 bg-white text-[#111418] shadow-sm' : 'border-transparent text-muted-foreground hover:bg-white/70'
    }`}
  >
    {icon}
    <span className="hidden max-w-[140px] truncate lg:inline">{label}</span>
  </button>
);
